import React from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { spaces, palette, borderRadius, boxShadows } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';

interface AIToolsFormStickyFooterProps {
  onCancel: () => void;
  onSubmit: () => void;
  cancelDisabled?: boolean;
  submitDisabled?: boolean;
}

const stickyFooterOuterStyles = {
  display: 'flex',
  padding: `0 ${spaces.md}`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
  width: '100%',
  position: 'sticky',
  bottom: 0,
  backgroundColor: palette.background.paperElevation2,
  zIndex: 1000,
  marginTop: 'auto',
};

const getStickyFooterInnerStyles = (isXsScreen: boolean) => ({
  display: 'flex',
  padding: `${spaces.lg} ${isXsScreen ? '0' : spaces.xxxxxl}`,
  justifyContent: isXsScreen ? 'center' : 'flex-end',
  alignItems: 'center',
  gap: spaces.xs,
  alignSelf: 'stretch',
});

const cancelButtonStyles = {
  display: 'flex',
  padding: `${spaces.xs} ${spaces.lg}`,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: borderRadius.sm,
  border: `1px solid ${palette.primary.outlinedBorder}`,
  textTransform: 'none',
  '&:hover': {
    border: `1px solid ${palette.primary.main}`,
    backgroundColor: palette.primary.hover,
  },
};

const generateButtonStyles = {
  display: 'flex',
  padding: `${spaces.xs} ${spaces.lg}`,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: borderRadius.sm,
  background: palette.primary.main,
  boxShadow: boxShadows.elevation4,
  textTransform: 'none',
  '&:hover': {
    background: palette.primary.dark,
  },
  '&:disabled': {
    background: palette.action.disabledBackground,
    color: palette.action.disabled,
  },
};

const AIToolsFormStickyFooter: React.FC<AIToolsFormStickyFooterProps> = ({
  onCancel,
  onSubmit,
  cancelDisabled = false,
  submitDisabled = false,
}) => {
  const { t } = useTranslation();
  const { isXsScreen } = useScreenSize();

  return (
    <Box sx={stickyFooterOuterStyles}>
      <Box sx={getStickyFooterInnerStyles(isXsScreen)}>
        <Button
          variant='outlined'
          onClick={onCancel}
          disabled={cancelDisabled}
          type='button'
          aria-label={t('Cancel')}
          aria-disabled={cancelDisabled}
          sx={cancelButtonStyles}
        >
          {t('Cancel')}
        </Button>
        <Button
          data-testid='ai-assistant-template-prompt-form-generate-button'
          variant='contained'
          type='submit'
          disabled={submitDisabled}
          onClick={onSubmit}
          aria-label={t('Generate')}
          aria-busy={submitDisabled}
          aria-disabled={submitDisabled}
          sx={generateButtonStyles}
        >
          {t('Generate')}
        </Button>
      </Box>
    </Box>
  );
};

export default AIToolsFormStickyFooter;

